import {  useState, useEffect, useMemo, useRef, useCallback} from 'react'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import html2canvas from 'html2canvas'
import {LineChart, Line, XAxis, YAxis, CartesianGrid,Tooltip, Legend, ResponsiveContainer, BarChart, Bar} from 'recharts'
import toast, { Toaster } from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useSensorData } from '../context/SensorDataContext'
import {iotControllerApi, sensorDataApi, organizationApi,locationApi, departmentApi, assetApi, parameterLibraryApi} from '../api/services'

// ─── Constants ────────────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { id: 'day',        label: 'Day Report' },
  { id: 'week',       label: 'Week Report' },
  { id: 'hour',       label: 'Hour Report' },
  { id: 'comparison', label: 'Comparison Report' },
]

const CHART_COLORS = [
  '#3b82f6','#22c55e','#f97316','#8b5cf6',
  '#06b6d4','#ec4899','#f59e0b','#ef4444','#14b8a6','#a855f7',
]

const SHIFT_COLORS = ['amber','blue','violet','emerald','rose']
const SHIFT_BADGE = {
  amber:   'bg-amber-50  text-amber-700  border-amber-200',
  blue:    'bg-blue-50   text-blue-700   border-blue-200',
  violet:  'bg-violet-50 text-violet-700 border-violet-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rose:    'bg-rose-50   text-rose-700   border-rose-200',
}

const API_BASE = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000'
const FULL_DAY = [{ id: 1, name: 'Full Day', start: '00:00', end: '23:59' }]
const MAX_ROW_PREVIEW = 7

// ─── Date/Shift utilities ────────────────────────────────────────────────────

const DateUtils = {
  pad:       n => String(n).padStart(2, '0'),
  todayStr:  () => { const d = new Date(); return `${d.getFullYear()}-${DateUtils.pad(d.getMonth()+1)}-${DateUtils.pad(d.getDate())}` },
  toInputDT: d => { const p = DateUtils.pad; return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}` },
  defaultFrom: () => { const d = new Date(); d.setHours(0,0,0,0); return DateUtils.toInputDT(d) },
  defaultTo:   () => DateUtils.toInputDT(new Date()),
  addDays: (dateStr, n) => { const d = new Date(`${dateStr}T00:00`); d.setDate(d.getDate()+n); return `${d.getFullYear()}-${DateUtils.pad(d.getMonth()+1)}-${DateUtils.pad(d.getDate())}` },
  parseUTC: str => {
    if (!str) return new Date()
    if (/[Z+\-]\d*$/.test(str)) return new Date(str)
    return new Date(str + 'Z')
  },
}

const ShiftUtils = {
  toMins: t => { const [h,m] = t.split(':').map(Number); return h*60+m },
  fmtMins: m => `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`,
  to12: t => { if (!t) return ''; const [h,m] = t.split(':').map(Number); const p = h>=12?'PM':'AM'; const hr = h%12||12; return `${hr}:${String(m).padStart(2,'0')} ${p}` },
  getShift: (dt, shifts) => {
    const mins = dt.getHours()*60 + dt.getMinutes()
    for (const s of shifts) {
      const start = ShiftUtils.toMins(s.start), end = ShiftUtils.toMins(s.end)
      if (start < end ? (mins >= start && mins < end) : (mins >= start || mins < end)) return s
    }
    return shifts[0]
  },
}

// ─── processReadings ─────────────────────────────────────────────────────────

const processReadings = (readings, shifts) => {
  // Production-day boundary = first shift's start. A reading whose clock time is
  // before the boundary (e.g. the 00:00–08:00 third shift) belongs to the
  // PREVIOUS calendar day's production day, so all three shifts report together.
  const boundaryMins = ShiftUtils.toMins(shifts[0]?.start || '00:00')
  return readings.map(r => {
    const data  = r.dynamic_payload?.Data || {}
    const rawTS = r.dynamic_payload?.Timestamp || r.recorded_at
    const dt    = DateUtils.parseUTC(rawTS)
    const shift = ShiftUtils.getShift(dt, shifts)
    const prod  = new Date(dt)
    if (boundaryMins > 0 && (dt.getHours()*60 + dt.getMinutes()) < boundaryMins)
      prod.setDate(prod.getDate() - 1)
    return {
      time:      dt.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:true }),
      date:      prod.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }),
      shiftId:   shift.id,
      shiftName: shift.name,
      recorded_at: rawTS,
      machine:    r.__machine || '',                       // asset (table grouping / summary)
      controller: r.__controller || r.__machine || '',     // device (table column + chart series)
      series:     r.__controller || r.__machine || '',
      placement:  r.__placement || '',                     // asset internal location (room/zone)
      ...data,
    }
  })
}

// ─── Small presentational components ─────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">{children}</p>
)

const ShiftBadge = ({ shiftId, shiftName, start, end, count }) => {
  const color = SHIFT_BADGE[SHIFT_COLORS[(shiftId - 1) % SHIFT_COLORS.length]]
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-semibold ${color}`}>
      <span>{shiftName}</span>
      {start && <span className="font-mono opacity-70">{start}–{end}</span>}
      {count != null && <span className="opacity-60">· {count}</span>}
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-gray-900 text-white rounded-lg px-3 py-2 shadow-2xl min-w-[160px]">
      <p className="text-[10px] text-gray-300 mb-1">{label}</p>
      {payload.map(p => {
        // When the chart is normalised, show the real (raw) value, not the 0–100%
        const raw = p.payload?.[`${p.dataKey}__raw`]
        const shown = raw != null ? raw : p.value
        return (
          <div key={p.dataKey} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-[10px] text-gray-300">{p.name}</span>
            </div>
            <span className="text-[10px] font-mono font-bold">
              {shown != null ? Number(shown).toFixed(2) : '—'}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ─── EmptyState — contextual fallbacks ───────────────────────────────────────

const EMPTY_STATES = {
  no_tenant: {
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    title: 'No organisation selected',
    body:  'Go to Org Management and select an organisation to view reports.',
    cta:   null,
  },
  no_controllers: {
    icon: 'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18',
    title: 'No controllers found',
    body:  'Add IoT controllers to your organisation before generating reports.',
    cta:   null,
  },
  idle: {
    icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    title: 'Set filters and generate a report',
    body:  'Choose a scope (location, department, or machine) and a date range, then click Generate Report.',
    cta:   null,
  },
  no_data: {
    icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4',
    title: 'No readings in this range',
    body:  'The selected machines sent no data during this period. Try widening the date range.',
    cta:   null,
  },
  no_params: {
    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6m14 0v-10a2 2 0 00-2-2h-2a2 2 0 00-2 2v10m-6 0h12',
    title: 'No parameter mappings',
    body:  'Configure parameter display names in IoT Controllers → Params.',
    cta:   null,
  },
  cmp_pick: {
    icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
    title: 'Pick two machines to compare',
    body:  'Select Machine A and Machine B in the filter panel, then click Generate Report.',
    cta:   null,
  },
}

const EmptyState = ({ type = 'idle', onRetry }) => {
  const s = EMPTY_STATES[type] || EMPTY_STATES.idle
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-gray-200 border-dashed">
      <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-4">
        <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={s.icon} />
        </svg>
      </div>
      <p className="text-sm font-bold text-gray-700 mb-1">{s.title}</p>
      <p className="text-xs text-gray-400 max-w-xs leading-relaxed">{s.body}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 px-4 py-2 text-xs font-semibold bg-blue-900 text-white rounded-xl hover:bg-blue-800 transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  )
}

// ─── TableSkeleton ────────────────────────────────────────────────────────────

const TableSkeleton = ({ cols = 5, rows = 6 }) => (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
    <div className="h-12 bg-gray-50 border-b border-gray-100" />
    <table className="w-full">
      <thead>
        <tr className="border-b border-gray-100">
          {Array.from({ length: cols }).map((_, i) => (
            <th key={i} className="px-4 py-3">
              <div className="h-3 bg-gray-200 rounded-full w-16 mx-auto" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r} className="border-b border-gray-100">
            {Array.from({ length: cols }).map((_, c) => (
              <td key={c} className="px-4 py-3">
                <div className={`h-3 rounded-full bg-gray-100 ${c === 0 ? 'w-24' : 'w-14 ml-auto'}`} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

// ─── 12-hour DateTimePicker ───────────────────────────────────────────────────

const DateTimePicker12h = ({ value, onChange, className }) => {
  const [date, setDate]   = useState('')
  const [hour, setHour]   = useState('12')
  const [min,  setMin]    = useState('00')
  const [ampm, setAmpm]   = useState('AM')

  useEffect(() => {
    if (!value) return
    const [d, t] = value.split('T')
    const [h, m] = (t || '00:00').split(':').map(Number)
    setDate(d || '')
    setMin(String(m).padStart(2, '0'))
    if      (h === 0)  { setHour('12'); setAmpm('AM') }
    else if (h < 12)   { setHour(String(h)); setAmpm('AM') }
    else if (h === 12) { setHour('12'); setAmpm('PM') }
    else               { setHour(String(h-12)); setAmpm('PM') }
  }, [value])

  const emit = (d, h, m, ap) => {
    let h24 = parseInt(h, 10)
    if (ap === 'AM' && h24 === 12) h24 = 0
    if (ap === 'PM' && h24 !== 12) h24 += 12
    onChange(`${d}T${String(h24).padStart(2,'0')}:${m}`)
  }

  const fld = 'text-sm text-gray-700 bg-white outline-none px-1.5 py-2 border-r border-gray-100 last:border-r-0'
  return (
    <div className={`flex items-center ${className || ''}`}>
      <input type="date" value={date} className={`${fld} min-w-[120px]`}
        onChange={e => { setDate(e.target.value); emit(e.target.value, hour, min, ampm) }} />
      <select value={hour} className={fld} style={{ width:48 }}
        onChange={e => { setHour(e.target.value); emit(date, e.target.value, min, ampm) }}>
        {Array.from({length:12},(_,i)=>String(i+1)).map(h =>
          <option key={h} value={h}>{h.padStart(2,'0')}</option>)}
      </select>
      <span className="text-gray-300 text-sm select-none">:</span>
      <select value={min} className={fld} style={{ width:48 }}
        onChange={e => { setMin(e.target.value); emit(date, hour, e.target.value, ampm) }}>
        {Array.from({length:60},(_,i)=>String(i).padStart(2,'0')).map(m =>
          <option key={m} value={m}>{m}</option>)}
      </select>
      <select value={ampm} className={`${fld} pr-2`} style={{ width:52 }}
        onChange={e => { setAmpm(e.target.value); emit(date, hour, min, e.target.value) }}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  )
}

// ─── ReportHeader ─────────────────────────────────────────────────────────────

const ReportHeader = ({ orgName, orgLogoBase64, orgLogo, scopeType, scopeName, fromDT, toDT, total }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
    {/* Top bar */}
    <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
      <div className="flex items-center gap-3">
        {orgLogoBase64 || orgLogo
          ? <img src={orgLogoBase64 || orgLogo} alt="org" className="h-9 w-auto max-w-[110px] object-contain rounded brightness-0 invert" />
          : <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center font-black text-sm">{(orgName||'Q').charAt(0)}</div>
        }
        <div>
          <p className="text-sm font-black tracking-tight">{orgName || 'QEDGE'}</p>
          <p className="text-[10px] text-blue-200">Production Report</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-[10px] text-blue-300">Generated</p>
        <p className="text-xs font-semibold">
          {new Date().toLocaleString('en-US', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true })}
        </p>
      </div>
    </div>
    {/* Meta grid */}
    <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-100">
      {[
        { label: 'Scope Type', value: scopeType },
        { label: 'Scope Name', value: scopeName },
        { label: 'Interval',   value: `${fromDT} → ${toDT}`, mono: true },
        { label: 'Records',    value: total },
      ].map(({ label, value, mono }) => (
        <div key={label} className="px-5 py-3.5">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
          <p className={`font-bold text-gray-800 text-xs ${mono ? 'font-mono' : ''}`}>{value}</p>
        </div>
      ))}
    </div>
  </div>
)

// ─── Scope Hierarchy panel ────────────────────────────────────────────────────
// Renders the scope's place in the org tree (parents), the entities contained
// under it (children), and the included-in-report counts. Driven entirely by
// reportModel.hierarchy so the on-screen panel and the PDF stay identical.
const HierarchyList = ({ title, items }) => (
  items && items.length ? (
    <div className="mb-3">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{title}</p>
      <ul className="space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-gray-700 flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />{it}
          </li>
        ))}
      </ul>
    </div>
  ) : null
)

const HierarchyPanel = ({ h }) => {
  if (!h) return null
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <h3 className="text-sm font-bold text-gray-800 mb-3">Scope Hierarchy</h3>

      {/* Parent path + the scope itself */}
      <div className="space-y-1 mb-4">
        {h.parents.map((p, i) => (
          <div key={i} className="flex text-xs">
            <span className="w-28 flex-shrink-0 text-gray-400">{p.level}</span>
            <span className="font-semibold text-gray-700">{p.name}</span>
          </div>
        ))}
        <div className="flex text-xs pt-0.5">
          <span className="w-28 flex-shrink-0 text-blue-500 font-semibold">{h.scopeType}</span>
          <span className="font-bold text-blue-800">{h.scopeName}</span>
        </div>
      </div>

      {/* Contained entities */}
      {h.show.sites       && <HierarchyList title="Sites"       items={h.lists.sites} />}
      {h.show.departments && <HierarchyList title="Departments" items={h.lists.departments} />}
      {h.show.assets      && <HierarchyList title="Assets"      items={h.lists.assets} />}
      {h.show.controllers && <HierarchyList title="Controllers" items={h.lists.controllers} />}

      {/* Counts */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Included in Report</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {[
            ['Sites',       h.counts.sites],
            ['Departments', h.counts.departments],
            ['Assets',      h.counts.assets],
            ['Controllers', h.counts.controllers],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between">
              <span className="text-gray-500">{k}</span>
              <span className="font-bold text-gray-800">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── useReportFilters hook ────────────────────────────────────────────────────

const useReportFilters = ({ controllers, assets, locations, departments }) => {
  // Hierarchy: Site (location) › Department › Asset › Controller
  const [selLocation,   setSelLocation]   = useState('')   // Site
  const [selDepartment, setSelDepartment] = useState('')
  const [selAsset,      setSelAsset]      = useState('')
  const [selectedController,  setSelectedController]  = useState('')
  const [selectedControllerB, setSelectedControllerB] = useState('')

  const assetById = useMemo(() => Object.fromEntries(assets.map(a => [String(a.id), a])), [assets])

  const ctrlAssets  = useCallback(c => (c.asset_ids||[]).map(id => assetById[String(id)]).filter(Boolean), [assetById])
  const ctrlLocIds  = useCallback(c => { const s=new Set(); ctrlAssets(c).forEach(a=>{ if(a.location_id!=null) s.add(String(a.location_id)) }); if(c.location_id!=null) s.add(String(c.location_id)); return [...s] }, [ctrlAssets])

  const matchScope = useCallback((c, s) => {
    if (s.location   && !ctrlLocIds(c).includes(String(s.location)))                             return false
    if (s.department && !ctrlAssets(c).some(a => String(a.department_id)===String(s.department))) return false
    if (s.asset      && !(c.asset_ids||[]).map(String).includes(String(s.asset)))                return false
    return true
  }, [ctrlLocIds, ctrlAssets])

  const scope = useMemo(() => ({ location:selLocation, department:selDepartment, asset:selAsset }), [selLocation,selDepartment,selAsset])
  const countCtrls = useCallback(s => controllers.filter(c => matchScope(c,s)).length, [controllers, matchScope])

  const scopedLocations   = useMemo(() => locations
    .map(l => ({ ...l, _count: countCtrls({ location:l.id }) })), [locations, countCtrls])
  const scopedDepartments = useMemo(() => departments.filter(d => {
    if (selLocation && String(d.location_id)!==String(selLocation)) return false
    return true
  }).map(d => ({ ...d, _count: countCtrls({ location:selLocation, department:d.id }) })), [departments, selLocation, countCtrls])
  const scopedAssets      = useMemo(() => assets.filter(a => {
    if (selLocation   && String(a.location_id)!==String(selLocation))   return false
    if (selDepartment && String(a.department_id)!==String(selDepartment)) return false
    return true
  }).map(a => ({ ...a, _count: countCtrls({ location:selLocation, department:selDepartment, asset:a.id }) })), [assets, selLocation, selDepartment, countCtrls])

  const filteredControllers = useMemo(() => controllers.filter(c => matchScope(c, scope)), [controllers, matchScope, scope])

  const controllerType = useCallback(c => {
    const firstId = (c?.asset_ids||[])[0]
    return firstId != null ? assetById[String(firstId)]?.asset_type || null : null
  }, [assetById])

  const machineName = useCallback(c => (c?.asset_names && c.asset_names[0]) || c?.name || '—', [])

  const comparableControllers = useMemo(() => {
    const ctrl = controllers.find(c => String(c.id)===String(selectedController))
    const typeA = ctrl ? controllerType(ctrl) : null
    return controllers.filter(c => String(c.id)!==String(selectedController) && (typeA ? controllerType(c)===typeA : true))
  }, [controllers, selectedController, controllerType])

  const scopeName = useMemo(() => {
    if (selAsset)      return assetById[selAsset]?.name || 'Asset'
    if (selDepartment) return departments.find(d=>String(d.id)===String(selDepartment))?.name || 'Department'
    if (selLocation)   return locations.find(l=>String(l.id)===String(selLocation))?.name || 'Site'
    return 'All assets'
  }, [selAsset,selDepartment,selLocation,assetById,departments,locations])

  const clearAll = useCallback(() => {
    setSelLocation(''); setSelDepartment(''); setSelAsset('')
    setSelectedController(''); setSelectedControllerB('')
  }, [])

  return {
    selLocation, setSelLocation, selDepartment, setSelDepartment,
    selAsset, setSelAsset,
    selectedController, setSelectedController,
    selectedControllerB, setSelectedControllerB,
    scopedLocations, scopedDepartments, scopedAssets,
    filteredControllers, comparableControllers,
    controllerType, machineName, scopeName,
    scopeCount: filteredControllers.length,
    clearAll,
  }
}

// ─── Main Reports component ───────────────────────────────────────────────────

const Reports = () => {
  const { user, activeTenant } = useAuth()
  const { paramMappingsCache, loadParamMappings } = useSensorData()

  const resolvedTenant = (typeof activeTenant === 'object' ? activeTenant?.id : activeTenant) ?? user?.tenant_id

  // ── Catalogue data ────────────────────────────────────────────────────────
  const [controllers,    setControllers]    = useState([])
  const [departments,    setDepartments]    = useState([])
  const [assets,         setAssets]         = useState([])
  const [locations,      setLocations]      = useState([])
  const [orgTree,        setOrgTree]        = useState([])
  const [orgsFlat,       setOrgsFlat]       = useState([])
  const [orgMap,         setOrgMap]         = useState({})
  const [libraryParams,  setLibraryParams]  = useState([])
  const [orgLogo,        setOrgLogo]        = useState('')
  const [orgLogoBase64,  setOrgLogoBase64]  = useState('')
  const [isLoadingCtrls, setIsLoadingCtrls] = useState(true)
  const [loadError,      setLoadError]      = useState(null)

  // ── Filters (from hook) ───────────────────────────────────────────────────
  const filters = useReportFilters({ controllers, assets, locations, departments })
  const {
    selLocation, setSelLocation, selDepartment, setSelDepartment,
    selAsset, setSelAsset,
    selectedController, setSelectedController,
    selectedControllerB, setSelectedControllerB,
    scopedLocations, scopedDepartments, scopedAssets,
    filteredControllers, comparableControllers,
    controllerType, machineName, scopeName, scopeCount, clearAll,
  } = filters

  // Scope as a readable breadcrumb (Site › Department › Asset › Controller)
  const scopeCrumb = useMemo(() => {
    const parts = []
    if (selLocation)        parts.push({ icon:'site',       name: locations.find(l => String(l.id)===String(selLocation))?.name })
    if (selDepartment)      parts.push({ icon:'dept',       name: departments.find(d => String(d.id)===String(selDepartment))?.name })
    if (selAsset)           parts.push({ icon:'asset',      name: assets.find(a => String(a.id)===String(selAsset))?.name })
    if (selectedController) parts.push({ icon:'controller', name: controllers.find(c => String(c.id)===String(selectedController))?.name })
    return parts.filter(p => p.name)
  }, [selLocation, selDepartment, selAsset, selectedController, locations, departments, assets, controllers])

  // ── Report config ─────────────────────────────────────────────────────────
  const [reportType,  setReportType]  = useState('day')
  const [rangeStart,  setRangeStart]  = useState(DateUtils.todayStr)   // single date (day/hour) & start (week)
  const [rangeEnd,    setRangeEnd]    = useState(DateUtils.todayStr)
  const [hourShift,   setHourShift]   = useState('all')                // chosen shift for the hour report ('all' = whole day)
  const [cmpFrom,     setCmpFrom]     = useState(DateUtils.todayStr)
  const [cmpTo,       setCmpTo]       = useState(DateUtils.todayStr)
  const [shifts,      setShifts]      = useState(FULL_DAY)
  const [selectedShift, setSelectedShift] = useState('all')
  const [cmpParam,    setCmpParam]    = useState('')
  const [csvParams,   setCsvParams]   = useState([])
  const [chartType,   setChartType]   = useState('line')
  const [chartMode,   setChartMode]   = useState('trend')   // 'trend' (asset) | 'average' (summary by asset)
  const [hiddenMachines, setHiddenMachines] = useState(() => new Set())

  // ── Fetch state ───────────────────────────────────────────────────────────
  const [rawReadings,  setRawReadings]  = useState([])
  const [rawReadingsB, setRawReadingsB] = useState([])
  const [effFrom,      setEffFrom]      = useState('')
  const [effTo,        setEffTo]        = useState('')
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [fetchError,    setFetchError]    = useState(null)
  const [fetched,       setFetched]       = useState(false)

  const [expandedGroups, setExpandedGroups] = useState({})
  const [filterOpen,     setFilterOpen]     = useState(false)
  const [chartMenuOpen,  setChartMenuOpen]  = useState(false)
  const filterRef = useRef()
  const chartMenuRef = useRef()
  const cmpChartRef = useRef()
  const chartRef  = useRef()
  const selectAllPendingRef = useRef(false)

  // ── Close filter on outside click ─────────────────────────────────────────
  useEffect(() => {
    const h = e => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false)
      if (chartMenuRef.current && !chartMenuRef.current.contains(e.target)) setChartMenuOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  // Keep the hour-report shift selection valid as shifts load/change ('all' is always valid)
  useEffect(() => {
    if (hourShift !== 'all' && !shifts.some(s => String(s.id) === String(hourShift))) setHourShift('all')
  }, [shifts, hourShift])

  // Auto-allocate a scope level when it has exactly ONE option; show a dropdown otherwise.
  useEffect(() => {
    if (!selLocation && scopedLocations.length === 1) setSelLocation(String(scopedLocations[0].id))
  }, [scopedLocations, selLocation, setSelLocation])
  useEffect(() => {
    if (!selAsset && scopedAssets.length === 1) setSelAsset(String(scopedAssets[0].id))
  }, [scopedAssets, selAsset, setSelAsset])
  useEffect(() => {
    if (reportType !== 'comparison' && !selectedController && filteredControllers.length === 1)
      setSelectedController(String(filteredControllers[0].id))
  }, [filteredControllers, selectedController, reportType, setSelectedController])

  // ── Load catalogue data ────────────────────────────────────────────────────
  const loadCatalogue = useCallback(() => {
    if (!resolvedTenant) { setIsLoadingCtrls(false); return }
    setIsLoadingCtrls(true); setLoadError(null)
    Promise.all([
      iotControllerApi.getAll(resolvedTenant),
      organizationApi.getAll({ tenantId: resolvedTenant }),
      locationApi.getAll({ tenantId: resolvedTenant }),
      departmentApi.getAll({ tenantId: resolvedTenant }).catch(() => ({ data: [] })),
      assetApi.getAll({ tenantId: resolvedTenant }).catch(() => ({ data: [] })),
    ]).then(([ctrlRes, orgRes, locRes, deptRes, assetRes]) => {
      const depts    = deptRes.data?.data  || deptRes.data  || []
      const assetList= assetRes.data?.data || assetRes.data || []
      const list     = ctrlRes.data?.data  || ctrlRes.data  || []
      const arr      = Array.isArray(list) ? list : []

      setDepartments(Array.isArray(depts) ? depts : [])
      setAssets(Array.isArray(assetList) ? assetList : (assetList.items || []))
      setControllers(arr)
      loadParamMappings(arr)
      // Leave the Controller filter on "All controllers" by default — the report
      // works at any scope level, so we don't pre-pick a single controller.

      const orgs = orgRes.data?.data || orgRes.data || []
      setOrgTree(Array.isArray(orgs) ? orgs : [])
      const orgById = {}; const flatOrgs = []
      const walkOrgs = (nodes, parentId = null) => {
        ;(Array.isArray(nodes) ? nodes : []).forEach(o => {
          orgById[o.id] = o
          flatOrgs.push({ id:o.id, name:o.company_name||o.name, parent_id:o.parent_id??parentId??null })
          if (o.children?.length) walkOrgs(o.children, o.id)
        })
      }
      walkOrgs(Array.isArray(orgs) ? orgs : [])
      setOrgsFlat(flatOrgs)

      const locTree = locRes.data?.data || locRes.data || []
      const locToOrg = {}; const flatLocs = []
      const walkLocs = nodes => {
        ;(Array.isArray(nodes) ? nodes : []).forEach(l => {
          const orgId = l.org_id ?? l.organization_id
          if (orgId && orgById[orgId]) locToOrg[l.id] = orgById[orgId]
          flatLocs.push(l)
          if (l.children?.length) walkLocs(l.children)
        })
      }
      walkLocs(Array.isArray(locTree) ? locTree : [])
      setOrgMap(locToOrg); setLocations(flatLocs)
    }).catch(err => {
      console.error('[Reports] catalogue load error:', err)
      setLoadError('Failed to load organisation data. Check your connection and try again.')
      toast.error('Failed to load data')
    }).finally(() => setIsLoadingCtrls(false))
  }, [resolvedTenant]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadCatalogue() }, [loadCatalogue])

  // ── Load org logo ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!resolvedTenant) return
    organizationApi.getAll({ tenantId: resolvedTenant }).then(res => {
      const orgs = res.data?.data || res.data || []
      const withLogo = (Array.isArray(orgs) ? orgs : []).find(o => o.logo_path)
      if (!withLogo) return
      const url = `${API_BASE}/api/organizations/${withLogo.id}/logo`
      setOrgLogo(url)
      fetch(url).then(r => r.blob()).then(blob => {
        const reader = new FileReader()
        reader.onloadend = () => setOrgLogoBase64(reader.result)
        reader.readAsDataURL(blob)
      }).catch(() => {})
    }).catch(() => {})
  }, [resolvedTenant])

  // ── Load parameter library ─────────────────────────────────────────────────
  useEffect(() => {
    parameterLibraryApi.getAll()
      .then(res => { const list = res.data?.data || res.data || []; setLibraryParams(Array.isArray(list) ? list : []) })
      .catch(() => {})
  }, [])

  // ── Derive shifts from org config ──────────────────────────────────────────
  const controller  = controllers.find(c => String(c.id) === String(selectedController))
  const controllerB = controllers.find(c => String(c.id) === String(selectedControllerB))

  // Comparison is within ONE asset: the controllers linked to the selected asset
  const assetControllers = useMemo(
    () => selAsset ? controllers.filter(c => (c.asset_ids||[]).map(String).includes(String(selAsset))) : [],
    [controllers, selAsset]
  )

  const reportControllers = useMemo(() => {
    if (selectedController) {
      const c = controllers.find(x => String(x.id)===String(selectedController))
      return c ? [c] : []
    }
    return filteredControllers
  }, [selectedController, controllers, filteredControllers])

  const activeControllers = useMemo(() => {
    if (reportType === 'comparison') return [controller, controllerB].filter(Boolean)
    return reportControllers
  }, [reportType, controller, controllerB, reportControllers])

  const repController = activeControllers[0] || null

  useEffect(() => {
    const rootOrg = (Array.isArray(orgTree) && orgTree[0]) || null
    const locOrg  = repController ? orgMap[repController.location_id] : null
    const hasRules = o => o && (o.shifts?.length || (o.shift_start_time && o.shift_end_time))
    const shiftOrg = hasRules(rootOrg) ? rootOrg : (hasRules(locOrg) ? locOrg : null)
    if (shiftOrg?.shifts?.length) {
      // Use exactly the shifts the user configured (1, 2 or 3 — any timing).
      setShifts(shiftOrg.shifts)
    } else if (shiftOrg?.shift_start_time && shiftOrg?.shift_end_time) {
      setShifts([{ id:1, name:'Shift 1', start:shiftOrg.shift_start_time, end:shiftOrg.shift_end_time }])
    } else {
      setShifts(FULL_DAY)
    }
    const logoOrg = locOrg || rootOrg
    setOrgLogo(logoOrg?.id && logoOrg?.logo_path ? `${API_BASE}/api/organizations/${logoOrg.id}/logo` : '')
  }, [repController, orgMap, orgTree])

  // ── Param derivation ───────────────────────────────────────────────────────
  const mappings = useMemo(() => {
    const merged = {}
    activeControllers.forEach(c => {
      const m = paramMappingsCache[c.id]
      if (m) Object.entries(m).forEach(([k,v]) => { if (!(k in merged)) merged[k] = v })
    })
    return merged
  }, [paramMappingsCache, activeControllers])

  const params = useMemo(() => {
    const dataKeys = new Set()
    rawReadings.forEach(r => Object.keys(r.dynamic_payload?.Data || {}).forEach(k => dataKeys.add(k)))
    const mapped = Object.entries(mappings).map(([key, m]) => ({
      key, label: m.display_name || key.toUpperCase(), unit: m.unit||'', min: m.min_value??null, max: m.max_value??null,
    }))
    const seenKeys = new Set(mapped.map(p => p.key))
    const fromLib  = libraryParams.filter(lp => !seenKeys.has(lp.param_key)).map(lp => {
      seenKeys.add(lp.param_key)
      return { key:lp.param_key, label:lp.display_name||lp.param_key.toUpperCase(), unit:lp.unit||'', min:null, max:null }
    })
    const fromData = [...dataKeys].filter(k => !seenKeys.has(k)).map(k => ({ key:k, label:k.toUpperCase(), unit:'', min:null, max:null }))
    return [...mapped, ...fromLib, ...fromData].sort((a,b) => a.key.localeCompare(b.key,undefined,{numeric:true,sensitivity:'base'}))
  }, [mappings, rawReadings, libraryParams])

  // Select all params when controller changes
  useEffect(() => { selectAllPendingRef.current = true; setCsvParams([]) }, [repController, reportType])
  useEffect(() => {
    if (selectAllPendingRef.current && params.length > 0) setCsvParams(params.map(p => p.key))
  }, [params])

  // Load 1 recent reading to discover keys
  useEffect(() => {
    if (!repController || !resolvedTenant) return
    sensorDataApi.getRecent(repController.id, resolvedTenant, 1).then(res => {
      const list = res.data?.data || res.data || []
      const sample = Array.isArray(list) ? list[0] : null
      if (!sample) return
      const keys = Object.keys(sample.dynamic_payload?.Data || {})
      if (!keys.length) return
      setLibraryParams(prev => {
        const existing = new Set(prev.map(p => p.param_key))
        const newOnes  = keys.filter(k => !existing.has(k)).map(k => ({ param_key:k, display_name:k.toUpperCase(), unit:'' }))
        return newOnes.length ? [...prev, ...newOnes] : prev
      })
      if (selectAllPendingRef.current) {
        selectAllPendingRef.current = false
        setCsvParams(prev => { const s=new Set(prev); const extra=keys.filter(k=>!s.has(k)); return extra.length ? [...prev,...extra] : prev })
      }
    }).catch(() => {})
  }, [repController, resolvedTenant]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived rows ───────────────────────────────────────────────────────────
  const allRows   = useMemo(() => processReadings(rawReadings, shifts),  [rawReadings, shifts])
  const rowsB     = useMemo(() => processReadings(rawReadingsB, shifts),  [rawReadingsB, shifts])
  const reportRows= useMemo(() => selectedShift === 'all' ? allRows : allRows.filter(r => String(r.shiftId)===String(selectedShift)), [allRows, selectedShift])

  const grouped = useMemo(() => {
    const map = {}
    reportRows.forEach(r => {
      if (!map[r.date]) map[r.date] = {}
      if (!map[r.date][r.shiftName]) map[r.date][r.shiftName] = { shiftId:r.shiftId, rows:[] }
      map[r.date][r.shiftName].rows.push(r)
    })
    return map
  }, [reportRows])

  const tableCols = csvParams.length ? params.filter(p => csvParams.includes(p.key)) : params

  const machinesList = useMemo(() => [...new Set(reportRows.map(r=>r.machine).filter(Boolean))], [reportRows]) // assets
  // Chart series = CONTROLLERS — so multiple controllers on one asset show as separate lines.
  const seriesList = useMemo(() => [...new Set(reportRows.map(r=>r.series).filter(Boolean))], [reportRows])
  const visibleSeries = useMemo(() => {
    const vis = seriesList.filter(s => !hiddenMachines.has(s))
    return vis.length ? vis : (seriesList.length ? [] : ['Series'])
  }, [seriesList, hiddenMachines])

  const seriesColor = i => CHART_COLORS[(i % CHART_COLORS.length + CHART_COLORS.length) % CHART_COLORS.length]
  const colorOf = s => seriesColor(seriesList.indexOf(s))

  // Aggregation granularity by report type (denser for hour, coarser for week)
  const bucketTarget = reportType === 'week' ? 168 : reportType === 'hour' ? 120 : 96

  // Time-bucket average per (controller × parameter) so dense data reads as smooth lines.
  const combinedChartData = useMemo(() => {
    if (!tableCols.length) return []
    const rows = reportRows.filter(r => !hiddenMachines.has(r.series))
    if (!rows.length) return []
    const times = rows.map(r => DateUtils.parseUTC(r.recorded_at).getTime())
    const minT = Math.min(...times), maxT = Math.max(...times)
    const span = Math.max(1, maxT - minT)
    const bucketMs = Math.max(1000, Math.ceil(span / bucketTarget))
    const buckets = new Map()
    rows.forEach(r => {
      const t = DateUtils.parseUTC(r.recorded_at).getTime()
      const idx = Math.floor((t - minT) / bucketMs)
      let b = buckets.get(idx); if (!b) { b = { t: minT + idx*bucketMs, vals:{} }; buckets.set(idx, b) }
      const sname = r.series || 'Series'
      tableCols.forEach(({ key:pk }) => {
        const v = Number(r[pk]); if (isNaN(v)) return
        const key = `${sname}__${pk}`
        if (!b.vals[key]) b.vals[key] = { sum:0, n:0 }
        b.vals[key].sum += v; b.vals[key].n += 1
      })
    })
    return [...buckets.values()].sort((a,b)=>a.t-b.t).map(b => {
      const row = { time: new Date(b.t).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',second:'2-digit'}) }
      Object.entries(b.vals).forEach(([key,{sum,n}]) => { row[key] = n ? Number((sum/n).toFixed(3)) : null })
      return row
    })
  }, [reportRows, tableCols, hiddenMachines, bucketTarget])

  // One trend panel per selected parameter; one line per CONTROLLER (series)
  const paramPanels = useMemo(() => tableCols.map(p => ({
    pk: p.key, label: p.label, unit: p.unit || '',
    lines: visibleSeries.map(s => ({ key: `${s}__${p.key}`, name: s, color: colorOf(s) })),
  })), [tableCols, visibleSeries]) // eslint-disable-line react-hooks/exhaustive-deps

  // Summary-by-asset (Location/Department/Division/Org scope): avg/min/max + count
  // per asset, aggregated across that asset's visible controllers.
  const summaryPanels = useMemo(() => {
    const keys = tableCols.map(c => c.key)
    const agg = {}   // asset -> { count, params: { pk: {sum,n,min,max} } }
    reportRows.forEach(r => {
      if (hiddenMachines.has(r.series)) return
      const a = r.machine || '—'
      if (!agg[a]) agg[a] = { count:0, params:{} }
      agg[a].count++
      keys.forEach(pk => {
        const v = Number(r[pk]); if (isNaN(v)) return
        const s = agg[a].params[pk] || (agg[a].params[pk] = { sum:0, n:0, min:Infinity, max:-Infinity })
        s.sum += v; s.n += 1; if (v < s.min) s.min = v; if (v > s.max) s.max = v
      })
    })
    const assets = machinesList.filter(a => agg[a])
    const r2 = v => Number(v.toFixed(2))
    const panels = tableCols.map(p => ({
      pk:p.key, label:p.label, unit:p.unit||'',
      data: assets.map(a => {
        const s = agg[a].params[p.key]
        return s?.n ? { asset:a, avg:r2(s.sum/s.n), min:r2(s.min), max:r2(s.max) } : { asset:a, avg:null, min:null, max:null }
      }),
    }))
    const countData = assets.map(a => ({ asset:a, value: agg[a].count }))
    return { panels, countData }
  }, [reportRows, tableCols, machinesList, hiddenMachines])

  // Distribution (histogram) of each selected parameter's values across visible controllers.
  const distributionPanels = useMemo(() => {
    const BINS = 12
    return tableCols.map(p => {
      const vals = []
      reportRows.forEach(r => { if (hiddenMachines.has(r.series)) return; const v = Number(r[p.key]); if (!isNaN(v)) vals.push(v) })
      if (!vals.length) return { pk:p.key, label:p.label, unit:p.unit||'', data:[] }
      const min = Math.min(...vals), max = Math.max(...vals)
      const w = ((max - min) || 1) / BINS
      const bins = Array.from({ length:BINS }, (_,i) => ({ lo:min+i*w, count:0 }))
      vals.forEach(v => { let i = Math.floor((v - min) / w); if (i >= BINS) i = BINS-1; if (i < 0) i = 0; bins[i].count++ })
      return { pk:p.key, label:p.label, unit:p.unit||'', data: bins.map(b => ({ range: b.lo.toFixed(1), count: b.count })) }
    })
  }, [reportRows, tableCols, machinesList, hiddenMachines]) // eslint-disable-line react-hooks/exhaustive-deps

  const cmpChartData = useMemo(() => {
    if (reportType !== 'comparison' || !cmpParam) return []
    const n = Math.max(allRows.length, rowsB.length)
    return Array.from({ length:n }, (_,i) => ({
      time: allRows[i]?.time || rowsB[i]?.time || '',
      A: allRows[i]?.[cmpParam] != null ? Number(allRows[i][cmpParam]) : null,
      B: rowsB[i]?.[cmpParam]   != null ? Number(rowsB[i][cmpParam])   : null,
    }))
  }, [reportType, cmpParam, allRows, rowsB])

  useEffect(() => { if (fetched && reportType==='comparison' && params.length && (!cmpParam || !params.some(p=>p.key===cmpParam))) setCmpParam(params[0].key) }, [reportType, fetched, params, cmpParam])

  // ── Stats helper ───────────────────────────────────────────────────────────
  const statsFor = (rows, key) => {
    const vals = rows.map(r => Number(r[key])).filter(v => !isNaN(v))
    if (!vals.length) return { count:0, avg:null, min:null, max:null }
    const sum = vals.reduce((s,v) => s+v, 0)
    return { count:vals.length, avg:sum/vals.length, min:Math.min(...vals), max:Math.max(...vals) }
  }

  // ── Scope hierarchy (parents · contained entities · counts) ──────────────────
  // Built dynamically from the catalog + the selected scope. Lives inside
  // reportModel so the UI panel and the PDF render from the same object.
  const hierarchyData = useMemo(() => {
    const locNameOf  = id => locations.find(l => String(l.id) === String(id))?.name || null
    const deptNameOf = id => departments.find(d => String(d.id) === String(id))?.name || null
    const assetOf    = id => assets.find(a => String(a.id) === String(id))
    const uniq = arr => [...new Set(arr.filter(Boolean))]

    const scopeType = selAsset ? 'Asset' : selDepartment ? 'Department' : selLocation ? 'Site' : 'Organization'

    // Everything under the current scope that actually carries controllers
    const inControllers = filteredControllers
    const assetIds = new Set()
    inControllers.forEach(c => (c.asset_ids || []).forEach(id => assetIds.add(String(id))))
    const inAssets = [...assetIds].map(id => assetOf(id)).filter(Boolean)
    const locIds = new Set()
    inAssets.forEach(a => { if (a.location_id != null) locIds.add(String(a.location_id)) })
    inControllers.forEach(c => { if (c.location_id != null) locIds.add(String(c.location_id)) })
    const deptIds = new Set(); inAssets.forEach(a => { if (a.department_id != null) deptIds.add(String(a.department_id)) })

    const counts = {
      sites: locIds.size, departments: deptIds.size,
      assets: inAssets.length, controllers: inControllers.length,
    }
    const lists = {
      sites:       uniq([...locIds].map(locNameOf)),
      departments: uniq([...deptIds].map(deptNameOf)),
      assets:      uniq(inAssets.map(a => a.name)),
      controllers: uniq(inControllers.map(c => c.name)),
    }

    // Parent path above the selected scope (Site › Department › Asset)
    let anchorLoc = null, anchorDept = null
    if (scopeType === 'Asset')           { const a = assetOf(selAsset); anchorLoc = a?.location_id; anchorDept = a?.department_id }
    else if (scopeType === 'Department') { const d = departments.find(x => String(x.id) === String(selDepartment)); anchorLoc = d?.location_id }

    const parents = []
    if (['Asset', 'Department'].includes(scopeType)) { const ln = locNameOf(anchorLoc); if (ln) parents.push({ level: 'Site', name: ln }) }
    if (scopeType === 'Asset')                       { const dn = deptNameOf(anchorDept); if (dn) parents.push({ level: 'Department', name: dn }) }

    // Which contained-entity lists to display, per scope level
    const show = {
      sites:       scopeType === 'Organization',
      departments: ['Organization', 'Site'].includes(scopeType),
      assets:      scopeType !== 'Asset',
      controllers: true,
    }

    return { scopeType, scopeName, parents, lists, show, counts }
  }, [locations, departments, assets, filteredControllers, scopeName, selLocation, selDepartment, selAsset])

  // ── Report view-model ───────────────────────────────────────────────────────
  // THE single source of truth. The on-screen tables AND the PDF both render
  // from this object, so the export can never diverge from what's displayed.
  const reportModel = useMemo(() => {
    const find = (arr, id) => arr.find(x => String(x.id) === String(id))
    // Scope breadcrumb: Site › Department › Asset
    const scopePath = []
    if (selLocation)   scopePath.push({ level:'Site',       name: find(locations, selLocation)?.name || String(selLocation) })
    if (selDepartment) scopePath.push({ level:'Department', name: find(departments, selDepartment)?.name || String(selDepartment) })
    if (selAsset)      scopePath.push({ level:'Asset',      name: find(assets, selAsset)?.name || String(selAsset) })
    const scopeType = selAsset ? 'Asset' : selDepartment ? 'Department' : selLocation ? 'Site' : 'Organization'

    // Date → Shift → Asset → Rows  (so each asset gets its own table, never merged)
    const groups = Object.entries(grouped).map(([date, shiftMap]) => ({
      date,
      shifts: Object.entries(shiftMap).sort((a,b) => a[1].shiftId - b[1].shiftId)
        .map(([shiftName, g]) => {
          const byAsset = {}
          g.rows.forEach(r => {
            const name = r.machine || '—'
            if (!byAsset[name]) byAsset[name] = { assetName:name, rows:[], ctrlIds:new Set(), placement:r.placement||'' }
            byAsset[name].rows.push(r)
            if (r.controller) byAsset[name].ctrlIds.add(r.controller)
            if (!byAsset[name].placement && r.placement) byAsset[name].placement = r.placement
          })
          const assets = Object.values(byAsset)
            .sort((a,b) => a.assetName.localeCompare(b.assetName))
            .map(a => {
              const controllerNames = [...a.ctrlIds]
              return { assetId:a.assetName, assetName:a.assetName, controllerCount:controllerNames.length, controllerNames, placement:a.placement, rows:a.rows }
            })
          return { shiftId:g.shiftId, shiftName, assets }
        }),
    }))
    const shiftLabel = selectedShift === 'all'
      ? 'All Shifts'
      : (shifts.find(s => String(s.id) === String(selectedShift))?.name || `Shift ${selectedShift}`)

    return {
      meta: {
        orgName: user?.org_name || activeTenant?.name || 'Organization',
        reportType, scopeType, scopeName, scopePath,
        scopePathLabel: scopePath.length ? scopePath.map(s => s.name).join(' › ') : 'All machines',
        from: (effFrom || '').slice(0,10),
        to:   (effTo   || '').slice(0,10),
        shiftLabel, total: reportRows.length, machines: machinesList.length || 1,
      },
      columns: tableCols.map(c => ({ key:c.key, label:c.label, unit:c.unit, min:c.min, max:c.max })),
      groups,
      hierarchy: hierarchyData,
    }
  }, [grouped, tableCols, selLocation, selDepartment, selAsset, scopeName, locations, departments, assets, reportType, effFrom, effTo, selectedShift, shifts, reportRows.length, machinesList.length, user, activeTenant, hierarchyData])

  // Comparison view-model — mirrors exactly what the comparison view shows:
  // the selected parameter's A / B / Δ stats.
  const comparisonModel = useMemo(() => {
    const sA = statsFor(allRows, cmpParam)
    const sB = statsFor(rowsB, cmpParam)
    const p  = params.find(x => x.key === cmpParam)
    return {
      paramLabel: p?.label || cmpParam,
      unit: p?.unit || '',
      rows: [
        { name: controller?.name  || 'A', ...sA, delta: null },
        { name: controllerB?.name || 'B', ...sB, delta: (sA.avg != null && sB.avg != null) ? sB.avg - sA.avg : null },
      ],
    }
  }, [allRows, rowsB, cmpParam, params, controller, controllerB]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── computeRange ──────────────────────────────────────────────────────────
  // Day/Week use a PRODUCTION-DAY window: [D @ shiftStart → (D±) @ shiftStart],
  // so the 00:00–08:00 shift is captured with the day it belongs to.
  const computeRange = () => {
    if (reportType === 'comparison') return [`${cmpFrom}T00:00`, `${cmpTo}T23:59`]
    const boundary  = ShiftUtils.toMins(shifts[0]?.start || '00:00')
    const startHHMM = ShiftUtils.fmtMins(boundary)
    if (reportType === 'hour') {
      // "All shifts" → the whole production day; a specific shift → its own window
      if (hourShift === 'all' || !shifts.length)
        return [`${rangeStart}T${startHHMM}`, `${DateUtils.addDays(rangeStart, 1)}T${startHHMM}`]
      const s = shifts.find(x => String(x.id) === String(hourShift)) || shifts[0]
      // Absolute datetime of a shift clock time on production day `rangeStart`
      // (times before the day boundary belong to the next calendar morning).
      const abs = clock => {
        const mins = ShiftUtils.toMins(clock)
        const d = new Date(`${rangeStart}T00:00`)
        d.setMinutes(mins)
        if (mins < boundary) d.setDate(d.getDate() + 1)
        return d
      }
      const from = abs(s.start)
      const to = abs(s.end)
      if (to <= from) to.setDate(to.getDate() + 1)   // shift crosses midnight
      return [DateUtils.toInputDT(from), DateUtils.toInputDT(to)]
    }
    // day → single date; week → range. End rolls to the next day's shift start.
    const endDate = reportType === 'week' ? rangeEnd : rangeStart
    return [`${rangeStart}T${startHHMM}`, `${DateUtils.addDays(endDate, 1)}T${startHHMM}`]
  }

  // ── fetchReadings ─────────────────────────────────────────────────────────
  const fetchReadings = async (ctrlId, from, to) => {
    try {
      const res = await sensorDataApi.getRange(ctrlId, resolvedTenant, from, to, 2000)
      const d = res.data?.data || res.data || []
      return Array.isArray(d) ? d : []
    } catch (err) {
      const status = err?.response?.status
      if (status === 404 || status === 405 || !status) {
        const res = await sensorDataApi.getRecent(ctrlId, resolvedTenant, 500)
        const all = res.data?.data || res.data || []
        const fromMs = new Date(from).getTime(), toMs = new Date(to).getTime()
        return (Array.isArray(all) ? all : []).filter(r => {
          const t = new Date(r.recorded_at + (r.recorded_at.endsWith('Z') ? '' : 'Z')).getTime()
          return t >= fromMs && t <= toMs
        })
      }
      throw err
    }
  }

  // ── handleFetch ────────────────────────────────────────────────────────────
  const handleFetch = async () => {
    const [from, to] = computeRange()
    if (!from || !to || new Date(from) >= new Date(to)) { toast.error('Invalid date range'); return }
    setIsLoadingData(true); setFetched(false); setFetchError(null); setHiddenMachines(new Set())
    // Scope-aware default: Asset → trend lines; broader scope → summary-by-asset
    setChartMode(selAsset ? 'trend' : 'average')
    try {
      if (reportType === 'comparison') {
        if (!selectedController || !selectedControllerB) { toast.error('Select Controller A and Controller B'); setIsLoadingData(false); return }
        const [a, b] = await Promise.all([fetchReadings(selectedController, from, to), fetchReadings(selectedControllerB, from, to)])
        setRawReadings(a.map(r => ({ ...r, __machine: controller?.name })))
        setRawReadingsB(b.map(r => ({ ...r, __machine: controllerB?.name })))
        if (!a.length && !b.length) toast('No data found for the selected range', { icon: 'ℹ️' })
      } else {
        const ctrls = reportControllers
        if (!ctrls.length) { toast.error('No controllers in the selected scope'); setIsLoadingData(false); return }
        const placementOf = c => assets.find(a => String(a.id) === String((c.asset_ids||[])[0]))?.placement || ''
        const results = await Promise.all(ctrls.map(c => fetchReadings(c.id, from, to).then(rd => rd.map(r => ({ ...r, __machine: machineName(c), __ctrlId: c.id, __controller: c.name, __placement: placementOf(c) })))))
        const all = results.flat().sort((x,y) => DateUtils.parseUTC(x.dynamic_payload?.Timestamp||x.recorded_at).getTime() - DateUtils.parseUTC(y.dynamic_payload?.Timestamp||y.recorded_at).getTime())
        setRawReadings(all)
        if (!all.length) toast('No data found for the selected range', { icon: 'ℹ️' })
      }
      setEffFrom(from); setEffTo(to)
      // Hour report is already scoped to one shift in the filter
      setSelectedShift(reportType === 'hour' ? hourShift : 'all')
      setFetched(true)
    } catch (err) {
      console.error('[Reports] fetch error:', err)
      const msg = err?.response?.data?.message || 'Failed to load sensor data'
      setFetchError(msg); toast.error(msg)
    } finally {
      setIsLoadingData(false)
    }
  }

  // ── handleClear ────────────────────────────────────────────────────────────
  const handleClear = () => {
    clearAll()
    setSelectedShift('all'); setRangeStart(DateUtils.todayStr()); setRangeEnd(DateUtils.todayStr())
    setHourShift('all')
    setCmpFrom(DateUtils.todayStr()); setCmpTo(DateUtils.todayStr())
    setRawReadings([]); setRawReadingsB([]); setFetched(false); setFetchError(null)
  }

  // ── downloadPDF ────────────────────────────────────────────────────────────
  // Renders ONLY from reportModel (+ the on-screen chart image). No re-derivation,
  // so the PDF is an exact representation of the current report view.
  const downloadPDF = useCallback(async () => {
    const { meta, columns, groups } = reportModel
    const ctrlName = meta.scopeName || 'report'
    const now = new Date()
    const p = n => String(n).padStart(2,'0')
    const stamp = `${now.getFullYear()}-${p(now.getMonth()+1)}-${p(now.getDate())}_${p(now.getHours())}-${p(now.getMinutes())}`
    const genTime = `${p(now.getDate())}.${p(now.getMonth()+1)}.${now.getFullYear()}   ${p(now.getHours())}:${p(now.getMinutes())}`
    const colDefs = columns.map(c => ({ key:c.key, label:`${c.label}${c.unit?`\n(${c.unit})`:''}` }))
    let chartImgData = null, chartAspect = 0.35
    if (chartRef.current) {
      try {
        const canvas = await html2canvas(chartRef.current, { scale:2, backgroundColor:'#ffffff', logging:false, useCORS:true })
        chartImgData = canvas.toDataURL('image/png'); chartAspect = canvas.height / canvas.width
      } catch (_) {}
    }
    const pdf = new jsPDF({ orientation:'landscape', unit:'mm', format:'a4' })
    const pageW = pdf.internal.pageSize.getWidth()
    const pageH = pdf.internal.pageSize.getHeight()
    let firstPage = true
    const startPage = titleLine => {
      if (!firstPage) pdf.addPage()
      firstPage = false
      let y = 10
      if (orgLogoBase64) pdf.addImage(orgLogoBase64,'PNG',10,y-2,30,14)
      pdf.setFontSize(13); pdf.setFont('helvetica','bold')
      pdf.text(meta.orgName.toUpperCase(), pageW/2, y+4, { align:'center' })
      y += 12
      pdf.setFontSize(9); pdf.setFont('helvetica','normal')
      pdf.text(titleLine, pageW/2, y, { align:'center' })
      pdf.text(genTime, pageW-12, y-8, { align:'right' })
      y += 5
      pdf.setDrawColor(0); pdf.setLineWidth(0.3); pdf.line(10,y,pageW-10,y); y += 5
      // Scope path + shift (matches the on-screen header)
      pdf.setFontSize(7.5); pdf.setFont('helvetica','normal')
      pdf.text(`${meta.scopeType} scope:  ${meta.scopePathLabel}   ·   Shift: ${meta.shiftLabel}`, 12, y)
      y += 5
      return y
    }

    // ── Scope hierarchy page: same data as the on-screen Hierarchy panel ──
    const h = reportModel.hierarchy
    if (h) {
      let y = startPage('Report Scope & Hierarchy')
      pdf.setFontSize(9); pdf.setFont('helvetica','bold'); pdf.text('Hierarchy Path', 12, y); y += 5
      pdf.setFontSize(8); pdf.setFont('helvetica','normal')
      h.parents.forEach(par => { pdf.text(par.level, 14, y); pdf.text(`: ${par.name}`, 52, y); y += 4.5 })
      pdf.setFont('helvetica','bold'); pdf.text(h.scopeType, 14, y); pdf.text(`: ${h.scopeName}`, 52, y); y += 8
      pdf.setFont('helvetica','normal')

      pdf.setFontSize(9); pdf.setFont('helvetica','bold'); pdf.text('Included in Report', 12, y); y += 5
      pdf.setFontSize(8); pdf.setFont('helvetica','normal')
      const c = h.counts
      const countPairs = [['Sites',c.sites],['Departments',c.departments],['Assets',c.assets],['Controllers',c.controllers]]
      countPairs.forEach((pair,i) => { pdf.text(`${pair[0]}: ${pair[1]}`, 14 + (i%3)*62, y + Math.floor(i/3)*4.5) })
      y += Math.ceil(countPairs.length/3)*4.5 + 5

      const blocks = [
        h.show.sites       && ['Sites',       h.lists.sites],
        h.show.departments && ['Departments', h.lists.departments],
        h.show.assets      && ['Assets',      h.lists.assets],
        h.show.controllers && ['Controllers', h.lists.controllers],
      ].filter(Boolean)
      blocks.forEach(([title, items]) => {
        if (!items || !items.length) return
        if (y > pageH - 18) { pdf.addPage(); y = 14 }
        pdf.setFont('helvetica','bold'); pdf.setFontSize(8); pdf.text(`${title} (${items.length})`, 12, y); y += 4.5
        pdf.setFont('helvetica','normal')
        const lines = pdf.splitTextToSize(items.join(',   '), pageW - 28)
        pdf.text(lines, 14, y); y += lines.length*4 + 4
      })
    }

    // ── Charts page: the exact on-screen chart grid, fit to one page ──
    if (chartImgData) {
      let y = startPage(`Production Report — ${ctrlName}`)
      const imgW = pageW - 20
      const imgH = Math.min(imgW * chartAspect, pageH - y - 8)
      pdf.addImage(chartImgData, 'PNG', 10, y, imgW, imgH)
    }

    // ── Data pages: the exact on-screen grouped tables, one page per date ──
    groups.forEach(group => {
      let cursorY = startPage(`Production Report on ${group.date}`)
      pdf.setFontSize(8); pdf.setFont('helvetica','bold')
      pdf.text(`Date : ${group.date}`, 12, cursorY)
      cursorY += 5
      group.shifts.forEach(shift => {
        pdf.setFillColor(220,220,220); pdf.rect(10,cursorY-4,pageW-20,6,'F')
        pdf.setFontSize(8); pdf.setFont('helvetica','bold')
        pdf.text(`Shift : ${shift.shiftName}`, 12, cursorY)
        pdf.text(`${shift.assets.length} asset${shift.assets.length===1?'':'s'}`, pageW-12, cursorY, { align:'right' })
        cursorY += 6
        // One table per asset (mirrors the on-screen per-asset cards)
        shift.assets.forEach(asset => {
          pdf.setFontSize(8); pdf.setFont('helvetica','bold'); pdf.setTextColor(30,58,138)
          pdf.text(`${asset.assetName}  ·  ${asset.controllerCount === 1 ? asset.controllerNames[0] : `${asset.controllerCount} controllers`}${asset.placement ? `  ·  ${asset.placement}` : ''}  ·  ${asset.rows.length} readings`, 12, cursorY)
          pdf.setTextColor(0); cursorY += 2
          const showCtrl = asset.controllerCount > 1
          autoTable(pdf, {
            startY: cursorY,
            head: [[...(showCtrl?['Controller']:[]), 'Time', ...colDefs.map(c=>c.label)]],
            body: asset.rows.map(row => [...(showCtrl?[row.controller||'']:[]), row.time, ...columns.map(c => row[c.key]!=null?Number(row[c.key]).toFixed(2):'—')]),
            margin: { left:10, right:10 },
            styles: { fontSize:7, cellPadding:1.5, halign:'center', lineColor:[180,180,180], lineWidth:0.2 },
            headStyles: { fillColor:[30,58,138], textColor:255, fontStyle:'bold', fontSize:7 },
            alternateRowStyles: { fillColor:[245,247,250] },
            columnStyles: { 0:{ halign:'left', fontStyle:'bold' } },
          })
          cursorY = pdf.lastAutoTable.finalY + 5
        })
      })
    })
    pdf.save(`${ctrlName.replace(/\s+/g,'_')}_${stamp}.pdf`)
  }, [reportModel, orgLogoBase64])

  // ── downloadComparisonPDF ──────────────────────────────────────────────────
  const downloadComparisonPDF = useCallback(async () => {
    const aName = controller?.name || 'A', bName = controllerB?.name || 'B'
    const now = new Date(); const p = n => String(n).padStart(2,'0')
    const stamp = `${now.getFullYear()}-${p(now.getMonth()+1)}-${p(now.getDate())}_${p(now.getHours())}-${p(now.getMinutes())}`
    const genTime = `${p(now.getDate())}.${p(now.getMonth()+1)}.${now.getFullYear()}   ${p(now.getHours())}:${p(now.getMinutes())}`
    let img = null, aspect = 0.4
    if (cmpChartRef.current) {
      try { const c = await html2canvas(cmpChartRef.current, { scale:2, backgroundColor:'#ffffff', logging:false, useCORS:true }); img = c.toDataURL('image/png'); aspect = c.height/c.width } catch (_) {}
    }
    const pdf = new jsPDF({ orientation:'landscape', unit:'mm', format:'a4' })
    const pageW = pdf.internal.pageSize.getWidth()
    let firstPage = true
    const startPage = titleLine => {
      if (!firstPage) pdf.addPage()
      firstPage = false
      let y = 10
      if (orgLogoBase64) pdf.addImage(orgLogoBase64,'PNG',10,y-2,30,14)
      pdf.setFontSize(13); pdf.setFont('helvetica','bold')
      pdf.text((user?.org_name||'Organization').toUpperCase(), pageW/2, y+4, { align:'center' })
      y += 12
      pdf.setFontSize(9); pdf.setFont('helvetica','normal')
      pdf.text(titleLine, pageW/2, y, { align:'center' })
      pdf.text(genTime, pageW-12, y-8, { align:'right' })
      y += 5
      pdf.setDrawColor(0); pdf.setLineWidth(0.3); pdf.line(10,y,pageW-10,y); y += 5
      return y
    }

    // Exactly what the comparison view shows: the overlay chart + the selected
    // parameter's A / B / Δ summary. Nothing more.
    let y = startPage(`Comparison Report — ${scopeName}`)
    pdf.setFontSize(8); pdf.setFont('helvetica','bold')
    pdf.text(`Asset : ${scopeName}   ·   ${aName}  vs  ${bName}`, 12, y)
    pdf.setFont('helvetica','normal')
    pdf.text(`Parameter : ${comparisonModel.paramLabel}${comparisonModel.unit?` (${comparisonModel.unit})`:''}`, pageW/2, y, { align:'center' })
    y += 5
    if (img) { const imgW = pageW-20, imgH = Math.min(imgW*aspect, 110); pdf.addImage(img,'PNG',10,y,imgW,imgH); y += imgH + 6 }
    const f = v => v==null ? '—' : Number(v).toFixed(2)
    autoTable(pdf, {
      startY: y,
      head: [['Machine','Readings','Average','Min','Max','Δ vs A']],
      body: comparisonModel.rows.map(r => [r.name, r.count, f(r.avg), f(r.min), f(r.max), r.delta==null ? '—' : `${r.delta>0?'+':''}${r.delta.toFixed(2)}`]),
      margin: { left:10, right:10 },
      styles: { fontSize:8, cellPadding:2.5, lineColor:[180,180,180], lineWidth:0.2 },
      headStyles: { fillColor:[30,58,138], textColor:255, fontStyle:'bold' },
      columnStyles: { 0:{ halign:'left', fontStyle:'bold' }, 1:{ halign:'right' }, 2:{ halign:'right' }, 3:{ halign:'right' }, 4:{ halign:'right' }, 5:{ halign:'right' } },
    })
    pdf.save(`comparison_${aName}_vs_${bName}_${stamp}.pdf`.replace(/\s+/g,'_'))
  }, [controller, controllerB, scopeName, comparisonModel, orgLogoBase64, user])

  const orgName = user?.org_name || activeTenant?.name || 'Organization'

  // ── Guard: no tenant ───────────────────────────────────────────────────────
  if (!resolvedTenant) return <EmptyState type="no_tenant" />

  // ── Guard: loading ─────────────────────────────────────────────────────────
  if (isLoadingCtrls) return (
    <div className="p-6 lg:p-8 min-h-screen bg-gray-50">
      <div className="mb-6"><div className="h-8 w-32 bg-gray-200 rounded-full animate-pulse mb-2" /><div className="h-4 w-64 bg-gray-100 rounded-full animate-pulse" /></div>
      <TableSkeleton cols={5} rows={8} />
    </div>
  )

  // ── Guard: load error ──────────────────────────────────────────────────────
  if (loadError) return (
    <div className="p-6 lg:p-8 min-h-screen bg-gray-50">
      <EmptyState type="idle" onRetry={loadCatalogue} />
      <p className="text-center text-xs text-red-500 mt-3">{loadError}</p>
    </div>
  )

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 lg:p-8 min-h-screen bg-gray-50 font-sans">
      <Toaster position="top-right" />

      {/* Page header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-800">Reports</h1>
          <p className="text-sm text-gray-500 mt-0.5">Historical sensor data by interval, shift and parameter</p>
        </div>
      </div>

      {/* ── Filter bar ───────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-6 print:hidden flex-wrap">
        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setFilterOpen(o => !o)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border font-semibold text-sm transition-all shadow-sm ${filterOpen ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" /></svg>
            Filter
            <svg className={`w-3.5 h-3.5 transition-transform ${filterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>

          {filterOpen && (
            <div className="absolute left-0 top-full mt-2 z-50 bg-white border border-gray-200 rounded-2xl shadow-xl p-5" style={{ minWidth:340, maxHeight:'85vh', overflowY:'auto' }}>

              {/* ── Report Type ── */}
              <SectionLabel>Report Type</SectionLabel>
              <div className="grid grid-cols-2 gap-1.5 mb-5">
                {REPORT_TYPES.map(rt => (
                  <button key={rt.id} onClick={() => { setReportType(rt.id); setFetched(false) }}
                    className={`py-2 px-3 rounded-xl text-xs font-semibold border transition-colors ${reportType===rt.id ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                    {rt.label}
                  </button>
                ))}
              </div>

              <div className="border-t border-gray-100 my-4" />

              {/* ── Scope ── */}
              <SectionLabel>Scope</SectionLabel>
              {[
                scopedLocations.length > 0 && {
                  label:'Site', value:selLocation, options:scopedLocations,
                  onChange:v => { setSelLocation(v); setSelDepartment(''); setSelAsset(''); setSelectedController(''); setFetched(false) },
                  getLabel: l => l.name, all:'All sites',
                },
                selLocation && scopedDepartments.length > 0 && {
                  label:'Department', value:selDepartment, options:scopedDepartments,
                  onChange:v => { setSelDepartment(v); setSelAsset(''); setSelectedController(''); setFetched(false) },
                  getLabel: d => d.name, all:'All departments',
                },
                scopedAssets.length > 0 && {
                  label:'Asset', value:selAsset, options:scopedAssets,
                  onChange:v => { setSelAsset(v); setSelectedController(''); setFetched(false) },
                  getLabel: a => `${a.name}${a.asset_type?' ('+a.asset_type+')':''}`, all:'All assets',
                },
                reportType !== 'comparison' && filteredControllers.length > 0 && {
                  label:'Controller', value:selectedController, options:filteredControllers,
                  onChange:v => { setSelectedController(v); setFetched(false) },
                  getLabel: c => c.name, all:'All controllers',
                },
              ].filter(Boolean).map(({ label, value, options, onChange, getLabel, all }) => (
                <div key={label} className="mb-3">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">{label}</label>
                  <select value={value} onChange={e => onChange(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:border-blue-400">
                    <option value="">{all}</option>
                    {options.map(o => <option key={o.id} value={o.id}>{getLabel(o)}</option>)}
                  </select>
                </div>
              ))}

              {/* Scope chip */}
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl mb-4">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" /></svg>
                <span className="text-xs font-semibold text-blue-800">
                  {reportType === 'comparison'
                    ? 'Select an asset, then two of its controllers to compare'
                    : `${scopeCrumb.length ? scopeCrumb.map(p=>p.name).join(' › ') : 'Whole organization'} · ${scopeCount} controller${scopeCount===1?'':'s'}`}
                </span>
              </div>

              {/* Comparison: controllers within the selected asset */}
              {reportType === 'comparison' && (
                <>
                  {!selAsset ? (
                    <p className="text-[11px] text-amber-600 mb-4 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                      Select an <b className="mx-1">Asset</b> above to compare its controllers.
                    </p>
                  ) : assetControllers.length < 2 ? (
                    <p className="text-[11px] text-amber-600 mb-4 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                      This asset has only {assetControllers.length} controller — add another to compare.
                    </p>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Controller A</label>
                        <select value={selectedController} onChange={e => { setSelectedController(e.target.value); if (e.target.value === selectedControllerB) setSelectedControllerB(''); setFetched(false) }}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:border-blue-400">
                          <option value="">Select controller A</option>
                          {assetControllers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div className="mb-4">
                        <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Controller B</label>
                        <select value={selectedControllerB} onChange={e => { setSelectedControllerB(e.target.value); setFetched(false) }}
                          disabled={!selectedController}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:border-blue-400 disabled:bg-gray-50">
                          <option value="">Select controller B</option>
                          {assetControllers.filter(c => String(c.id) !== String(selectedController)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="border-t border-gray-100 my-4" />

              {/* ── Date range ── */}
              <SectionLabel>{reportType === 'week' ? 'Date Range' : 'Date'}</SectionLabel>
              {reportType === 'day' && (
                <div className="mb-5">
                  <input type="date" value={rangeStart} onChange={e => { setRangeStart(e.target.value); setFetched(false) }}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white outline-none focus:border-blue-400" />
                </div>
              )}
              {reportType === 'week' && (
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {[['Start', rangeStart, setRangeStart],['End', rangeEnd, setRangeEnd]].map(([lbl,val,set]) => (
                    <div key={lbl}>
                      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">{lbl}</label>
                      <input type="date" value={val} onChange={e => { set(e.target.value); setFetched(false) }}
                        className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white outline-none focus:border-blue-400" />
                    </div>
                  ))}
                </div>
              )}
              {reportType === 'hour' && (
                <>
                  <div className="mb-4">
                    <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Date</label>
                    <input type="date" value={rangeStart} onChange={e => { setRangeStart(e.target.value); setFetched(false) }}
                      className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white outline-none focus:border-blue-400" />
                  </div>
                  <div className="mb-4">
                    <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Shift</label>
                    <select value={hourShift} onChange={e => { setHourShift(e.target.value); setFetched(false) }}
                      className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white outline-none focus:border-blue-400">
                      <option value="all">All shifts (whole day)</option>
                      {shifts.map(s => (
                        <option key={s.id} value={s.id}>{s.name} · {ShiftUtils.to12(s.start)}–{ShiftUtils.to12(s.end)}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {reportType === 'comparison' && (
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {[['From', cmpFrom, setCmpFrom],['To', cmpTo, setCmpTo]].map(([lbl,val,set]) => (
                    <div key={lbl}>
                      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">{lbl}</label>
                      <input type="date" value={val} onChange={e => { set(e.target.value); setFetched(false) }}
                        className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white outline-none focus:border-blue-400" />
                    </div>
                  ))}
                </div>
              )}

              {/* ── Parameters ── */}
              {params.length > 0 && (
                <>
                  <div className="border-t border-gray-100 my-4" />
                  <div className="flex items-center justify-between mb-2">
                    <SectionLabel>Parameters</SectionLabel>
                    <div className="flex items-center gap-2 text-[11px] -mt-3">
                      <button onClick={() => setCsvParams(params.map(p=>p.key))} className="text-blue-700 font-semibold hover:underline">All</button>
                      <span className="text-gray-300">|</span>
                      <button onClick={() => setCsvParams([])} className="text-gray-500 hover:underline">Clear</button>
                      <span className="text-gray-400 ml-1">{csvParams.length}/{params.length}</span>
                    </div>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                    {params.map(p => {
                      const checked = csvParams.includes(p.key)
                      return (
                        <label key={p.key} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer border transition-colors ${checked ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-100 hover:border-gray-200'}`}>
                          <input type="checkbox" checked={checked} className="w-4 h-4 accent-blue-800 rounded flex-shrink-0"
                            onChange={e => setCsvParams(prev => e.target.checked ? [...prev, p.key] : prev.filter(k=>k!==p.key))} />
                          <span className="text-sm font-semibold text-gray-700 flex-1">{p.label}</span>
                          {p.unit && <span className="text-xs text-gray-400">[{p.unit}]</span>}
                        </label>
                      )
                    })}
                  </div>
                </>
              )}

              {/* Hint: a scope must be chosen before generating */}
              {reportType !== 'comparison' && !(selLocation||selDepartment||selAsset||selectedController) && (
                <p className="mt-4 text-[11px] text-amber-600 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  Select a Site, Department, Asset or Controller to generate a report.
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2 mt-5">
                <button onClick={handleClear}
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Clear
                </button>
                <button onClick={() => { handleFetch(); setFilterOpen(false) }}
                  disabled={isLoadingData || (reportType==='comparison' ? (!selectedController||!selectedControllerB) : !(selLocation||selDepartment||selAsset||selectedController))}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-900 text-white text-sm font-semibold rounded-xl hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {isLoadingData
                    ? <><span className="w-3.5 h-3.5 border-2 border-blue-300 border-t-white rounded-full animate-spin" />Loading…</>
                    : <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>Generate Report</>
                  }
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Scope breadcrumb chip */}
        {reportType !== 'comparison' && (
          <span className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold rounded-xl">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" /></svg>
            {scopeCrumb.length === 0
              ? <span className="text-blue-500">Whole organization</span>
              : scopeCrumb.map((p, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    {i > 0 && <span className="text-blue-300">›</span>}
                    <span>{p.name}</span>
                  </span>
                ))}
            <span className="text-blue-400 font-normal">· {scopeCount} controller{scopeCount===1?'':'s'}</span>
          </span>
        )}
      </div>

      {/* ── No controllers ─────────────────────────────────────────────────── */}
      {controllers.length === 0 && !isLoadingData && (
        <EmptyState type="no_controllers" onRetry={loadCatalogue} />
      )}

      {/* ── Idle (not yet fetched) ──────────────────────────────────────────── */}
      {controllers.length > 0 && !fetched && !isLoadingData && !fetchError && (
        <EmptyState type={reportType==='comparison' && (!selectedController||!selectedControllerB) ? 'cmp_pick' : 'idle'} />
      )}

      {/* ── Loading skeleton ─────────────────────────────────────────────────── */}
      {isLoadingData && <TableSkeleton cols={tableCols.length + 2 || 6} rows={8} />}

      {/* ── Fetch error ───────────────────────────────────────────────────────── */}
      {fetchError && !isLoadingData && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-3">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          <div className="flex-1">
            <p className="text-sm font-bold text-red-800 mb-0.5">Could not load sensor data</p>
            <p className="text-xs text-red-600">{fetchError}</p>
          </div>
          <button onClick={handleFetch} className="text-xs font-semibold text-red-700 border border-red-300 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors flex-shrink-0">Retry</button>
        </div>
      )}

      {/* ── Report view controls ──────────────────────────────────────────────── */}
      {fetched && reportType !== 'comparison' && params.length > 0 && !isLoadingData && (
        <div className="flex items-center gap-2 mb-4 flex-wrap print:hidden">
          {/* Date chip */}
          <span className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-200 text-gray-600 text-xs font-medium rounded-xl shadow-sm">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            {effFrom.slice(0,10)} → {effTo.slice(0,10)}
          </span>
          <div className="w-px h-7 bg-gray-200" />

          {/* Shift selector — hour report is already scoped to a single shift in the filter */}
          {reportType !== 'hour' && (
            <div className="flex items-center gap-0 border border-gray-200 rounded-xl overflow-hidden bg-white hover:border-blue-400 shadow-sm transition-colors">
              <span className="px-2.5 py-2 bg-gray-50 border-r border-gray-100 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Shift</span>
              </span>
              <select value={selectedShift} onChange={e => setSelectedShift(e.target.value)}
                className="text-sm text-gray-700 px-2 py-2 bg-white outline-none min-w-[130px]">
                <option value="all">All Shifts</option>
                {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({ShiftUtils.to12(s.start)})</option>)}
              </select>
            </div>
          )}

          {/* Chart mode — Trend (per-controller lines) · Average · Min/Max · Distribution (by asset) */}
          <div className="flex items-center rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {[{ id:'trend', label:'Trend' },{ id:'average', label:'Average' },{ id:'minmax', label:'Min/Max' },{ id:'distribution', label:'Distribution' }].map(mo => (
              <button key={mo.id} onClick={() => setChartMode(mo.id)}
                className={`px-3 py-2 text-xs font-semibold transition-colors border-l first:border-l-0 border-gray-100 ${chartMode===mo.id ? 'bg-blue-900 text-white' : 'bg-white text-gray-500 hover:text-blue-600'}`}>
                {mo.label}
              </button>
            ))}
          </div>

          {/* Chart type + Download */}
          <div className="flex items-center gap-2 ml-auto">
            {[{ id:'line', d:'M3 12h4l3-8 4 16 3-10h4' },{ id:'bar', d:'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6m14 0v-10a2 2 0 00-2-2h-2a2 2 0 00-2 2v10M3 19h18' }].map(t => (
              <button key={t.id} onClick={() => setChartType(t.id)}
                className={`p-2 rounded-xl border shadow-sm transition-colors ${chartType===t.id ? 'bg-blue-900 border-blue-900 text-white' : 'bg-white border-gray-200 text-gray-400 hover:border-blue-400 hover:text-blue-600'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.d} /></svg>
              </button>
            ))}
            <button onClick={() => downloadPDF()}
              disabled={!fetched || reportRows.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-900 text-white text-sm font-semibold rounded-xl border border-blue-900 shadow-sm hover:bg-blue-800 disabled:opacity-50 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Download PDF {csvParams.length > 0 && csvParams.length < params.length ? `(${csvParams.length})` : ''}
            </button>
          </div>
        </div>
      )}

      {/* ── Report content: standard ──────────────────────────────────────────── */}
      {fetched && reportType !== 'comparison' && !isLoadingData && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 items-start">
            <div className="lg:col-span-2">
              <ReportHeader
                orgName={orgName} orgLogo={orgLogo} orgLogoBase64={orgLogoBase64}
                scopeType={reportModel.meta.scopeType}
                scopeName={reportModel.meta.scopeName}
                fromDT={(effFrom||'').replace('T',' ')} toDT={(effTo||'').replace('T',' ')}
                total={reportRows.length}
              />
            </div>
            <HierarchyPanel h={reportModel.hierarchy} />
          </div>

          {/* ── Controller filter (applies to all panels) ── */}
          {seriesList.length > 1 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-3 mb-4 flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mr-1">Controllers</span>
              {seriesList.map(s => {
                const on = !hiddenMachines.has(s)
                return (
                  <label key={s} className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs cursor-pointer transition-colors ${on ? 'bg-white border-gray-200 text-gray-700' : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
                    <input type="checkbox" checked={on} className="w-3.5 h-3.5 accent-blue-700"
                      onChange={e => setHiddenMachines(prev => { const n = new Set(prev); if (e.target.checked) n.delete(s); else n.add(s); return n })} />
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorOf(s) }} />
                    {s}
                  </label>
                )
              })}
              <span className="text-[11px] text-gray-400 ml-auto">{reportRows.length} records · time-averaged</span>
            </div>
          )}

          {/* ── Chart grid: one panel per parameter (each auto-scaled) ── */}
          <div ref={chartRef} className="mb-6">
            {tableCols.length === 0 || reportRows.length < 2
              ? <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5"><EmptyState type="no_data" /></div>
              : chartMode === 'average'
              ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                    <h3 className="text-xs font-bold text-gray-700 mb-2">Readings by asset</h3>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={summaryPanels.countData} margin={{ top:5,right:8,left:-22,bottom:0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                        <XAxis dataKey="asset" tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={44} />
                        <YAxis tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} width={38} />
                        <Tooltip />
                        <Bar dataKey="value" name="Readings" fill="#3b82f6" radius={[2,2,0,0]} isAnimationActive={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {summaryPanels.panels.map((panel, pi) => (
                    <div key={panel.pk} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                      <h3 className="text-xs font-bold text-gray-700 mb-2">Avg {panel.label}{panel.unit?` (${panel.unit})`:''} by asset</h3>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={panel.data} margin={{ top:5,right:8,left:-22,bottom:0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                          <XAxis dataKey="asset" tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={44} />
                          <YAxis tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} width={38} />
                          <Tooltip />
                          <Bar dataKey="avg" name={`Avg ${panel.label}`} fill={seriesColor(pi+1)} radius={[2,2,0,0]} isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
                </div>
              )
              : chartMode === 'minmax'
              ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {summaryPanels.panels.map(panel => (
                    <div key={panel.pk} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                      <h3 className="text-xs font-bold text-gray-700 mb-2">{panel.label}{panel.unit?` (${panel.unit})`:''} — Min / Avg / Max by asset</h3>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={panel.data} margin={{ top:5,right:8,left:-22,bottom:0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                          <XAxis dataKey="asset" tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={44} />
                          <YAxis tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} width={38} />
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize:'10px' }} />
                          <Bar dataKey="min" name="Min" fill="#93c5fd" radius={[2,2,0,0]} isAnimationActive={false} />
                          <Bar dataKey="avg" name="Avg" fill="#3b82f6" radius={[2,2,0,0]} isAnimationActive={false} />
                          <Bar dataKey="max" name="Max" fill="#1e3a8a" radius={[2,2,0,0]} isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
                </div>
              )
              : chartMode === 'distribution'
              ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {distributionPanels.map((panel, pi) => (
                    <div key={panel.pk} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                      <h3 className="text-xs font-bold text-gray-700 mb-2">{panel.label}{panel.unit?` (${panel.unit})`:''} — Distribution</h3>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={panel.data} margin={{ top:5,right:8,left:-22,bottom:0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                          <XAxis dataKey="range" tick={{ fontSize:8,fill:'#9ca3af' }} tickLine={false} axisLine={false} interval={1} />
                          <YAxis tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} width={38} allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="count" name="Readings" fill={seriesColor(pi)} radius={[2,2,0,0]} isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
                </div>
              )
              : combinedChartData.length < 2
              ? <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5"><EmptyState type="no_data" /></div>
              : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {paramPanels.map(panel => (
                    <div key={panel.pk} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-bold text-gray-700">{panel.label}{panel.unit ? ` (${panel.unit})` : ''}</h3>
                      </div>
                      <ResponsiveContainer width="100%" height={170}>
                        {chartType === 'line'
                          ? (
                            <LineChart data={combinedChartData} margin={{ top:5,right:8,left:-22,bottom:0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                              <XAxis dataKey="time" tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={40} />
                              <YAxis tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} width={38} />
                              <Tooltip content={<CustomTooltip />} cursor={{ stroke:'#e5e7eb',strokeWidth:1 }} />
                              {panel.lines.map(ln => (
                                <Line key={ln.key} type="monotone" dataKey={ln.key} name={ln.name} stroke={ln.color}
                                  strokeWidth={1.8} dot={false} connectNulls isAnimationActive={false} activeDot={{ r:3,strokeWidth:0 }} />
                              ))}
                            </LineChart>
                          ) : (
                            <BarChart data={combinedChartData} margin={{ top:5,right:8,left:-22,bottom:0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                              <XAxis dataKey="time" tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={40} />
                              <YAxis tick={{ fontSize:9,fill:'#9ca3af' }} tickLine={false} axisLine={false} width={38} />
                              <Tooltip content={<CustomTooltip />} />
                              {panel.lines.map(ln => (
                                <Bar key={ln.key} dataKey={ln.key} name={ln.name} fill={ln.color} radius={[2,2,0,0]} isAnimationActive={false} />
                              ))}
                            </BarChart>
                          )}
                      </ResponsiveContainer>
                      {panel.lines.length > 1 && (
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                          {panel.lines.map(ln => (
                            <span key={ln.key} className="flex items-center gap-1 text-[10px] text-gray-500">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ln.color }} />{ln.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            }
          </div>

          {/* ── Data tables ── */}
          {reportRows.length > 0 && tableCols.length === 0 && <EmptyState type="no_params" />}
          {reportRows.length === 0 && fetched && <EmptyState type="no_data" />}

          {reportRows.length > 0 && reportModel.columns.length > 0 && (
            <div className="space-y-6">
              {reportModel.groups.map(group =>
                group.shifts.map(shift => {
                  const shiftCfg = shifts.find(s => s.id === shift.shiftId)
                  return (
                    <div key={`${group.date}-${shift.shiftId}`} className="space-y-3">
                      {/* Date + Shift band */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-bold text-gray-800">{group.date}</span>
                        <ShiftBadge shiftId={shift.shiftId} shiftName={shift.shiftName} start={shiftCfg?.start} end={shiftCfg?.end}
                          count={`${shift.assets.length} asset${shift.assets.length===1?'':'s'}`} />
                      </div>

                      {/* One card per asset */}
                      {shift.assets.map(asset => {
                        const groupKey = `${group.date}-${shift.shiftId}-${asset.assetId}`
                        const isExpanded = expandedGroups[groupKey]
                        const visibleRows = isExpanded ? asset.rows : asset.rows.slice(0, MAX_ROW_PREVIEW)
                        return (
                          <div key={groupKey} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-blue-500" />
                                <p className="text-sm font-bold text-gray-800">{asset.assetName}</p>
                                <span className="text-[11px] text-gray-400">· {asset.controllerCount === 1 ? asset.controllerNames[0] : `${asset.controllerCount} controllers`}</span>
                                {asset.placement && <span className="text-[11px] text-gray-500">· 📍 {asset.placement}</span>}
                              </div>
                              <span className="text-[11px] bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">{asset.rows.length} readings</span>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-gray-50 border-b border-gray-100">
                                    {asset.controllerCount > 1 && <th className="px-4 py-2.5 text-left font-semibold text-gray-500 whitespace-nowrap">Controller</th>}
                                    <th className="px-4 py-2.5 text-left font-semibold text-gray-500 whitespace-nowrap">Time</th>
                                    {reportModel.columns.map(p => (
                                      <th key={p.key} className="px-4 py-2.5 text-right font-semibold text-gray-500 whitespace-nowrap">
                                        {p.label}{p.unit && <span className="font-normal text-gray-400 ml-0.5">[{p.unit}]</span>}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {visibleRows.map((row, i) => {
                                    const bg = i%2===0 ? 'bg-white' : 'bg-gray-50/60'
                                    return (
                                      <tr key={i} className={`${bg} border-b border-gray-100`}>
                                        {asset.controllerCount > 1 && <td className="px-4 py-2.5 font-semibold text-gray-600 whitespace-nowrap">{row.controller}</td>}
                                        <td className="px-4 py-2.5 font-mono text-gray-500 whitespace-nowrap">{row.time}</td>
                                        {reportModel.columns.map(p => {
                                          const val = row[p.key], num = parseFloat(val)
                                          const oor = !isNaN(num) && ((p.min!=null&&num<p.min)||(p.max!=null&&num>p.max))
                                          return (
                                            <td key={p.key} className={`px-4 py-2.5 text-right font-mono font-semibold whitespace-nowrap ${oor ? 'text-red-500 bg-red-50' : 'text-gray-700'}`}
                                              title={oor ? `Out of range (min ${p.min??'—'}, max ${p.max??'—'})` : undefined}>
                                              {val != null ? Number(val).toFixed(2) : '—'}
                                            </td>
                                          )
                                        })}
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {asset.rows.length > MAX_ROW_PREVIEW && (
                              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                                <p className="text-xs text-gray-500">Showing {isExpanded ? asset.rows.length : MAX_ROW_PREVIEW} of {asset.rows.length} readings</p>
                                <button onClick={() => setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                                  className="text-xs font-semibold text-blue-700 hover:text-blue-800">
                                  {isExpanded ? 'Show less ↑' : `Show all ${asset.rows.length} ↓`}
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Comparison report ─────────────────────────────────────────────────── */}
      {fetched && reportType === 'comparison' && !isLoadingData && (
        <div>
          {/* Header card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-6 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-900 to-blue-800 text-white flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-black">Machine Comparison</p>
                <p className="text-xs text-blue-200 mt-0.5">{effFrom.slice(0,10)} → {effTo.slice(0,10)}</p>
              </div>
              <div className="flex items-center gap-2">
                {[
                  { name:controller?.name || 'A',  count:allRows.length, color:'bg-blue-400' },
                  { name:controllerB?.name || 'B', count:rowsB.length,   color:'bg-emerald-400' },
                ].map((m, i) => (
                  <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 border border-white/20 text-white text-xs font-semibold rounded-lg">
                    <span className={`w-2 h-2 rounded-full ${m.color}`} />
                    {m.name} ({m.count})
                  </span>
                ))}
                <button onClick={downloadComparisonPDF} disabled={allRows.length===0 && rowsB.length===0}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-blue-900 text-xs font-bold rounded-lg hover:bg-blue-50 disabled:opacity-50">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Download PDF
                </button>
              </div>
            </div>
            {params.length > 0 && (
              <div className="px-6 py-3 border-t border-gray-100 flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500">Compare:</span>
                <select value={cmpParam} onChange={e => setCmpParam(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white outline-none focus:ring-2 focus:ring-blue-500 text-gray-700">
                  {params.map(p => <option key={p.key} value={p.key}>{p.label}{p.unit?` (${p.unit})`:''}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Overlay chart */}
          <div ref={cmpChartRef} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6">
            <h2 className="text-sm font-bold text-gray-700 mb-4">
              {params.find(p=>p.key===cmpParam)?.label || cmpParam} — A vs B
            </h2>
            {cmpChartData.length < 2 || !cmpParam
              ? <EmptyState type="no_data" />
              : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={cmpChartData} margin={{ top:5,right:10,left:-20,bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="time" tick={{ fontSize:10,fill:'#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={50} />
                    <YAxis tick={{ fontSize:10,fill:'#9ca3af' }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize:'11px',paddingTop:'8px' }} />
                    <Line type="monotone" dataKey="A" name={controller?.name || 'A'}  stroke="#3b82f6" strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                    <Line type="monotone" dataKey="B" name={controllerB?.name || 'B'} stroke="#22c55e" strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              )
            }
          </div>

          {/* Stats table with delta column */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <p className="text-sm font-bold text-gray-700">Summary Statistics</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-gray-500 text-xs">
                  <th className="px-4 py-2.5 text-left font-semibold">Machine</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Readings</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Average</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Min</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Max</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Δ vs A</th>
                </tr>
              </thead>
              <tbody>
                {comparisonModel.rows.map((r, i) => {
                  const f = v => v == null ? '—' : Number(v).toFixed(2)
                  const dot = i === 0 ? 'bg-blue-500' : 'bg-emerald-500'
                  return (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-4 py-3 font-semibold text-gray-700">
                        <span className="inline-flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${dot}`} />{r.name}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-600">{r.count}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-gray-800">{f(r.avg)}</td>
                      <td className="px-4 py-3 text-right font-mono text-gray-600">{f(r.min)}</td>
                      <td className="px-4 py-3 text-right font-mono text-gray-600">{f(r.max)}</td>
                      <td className={`px-4 py-3 text-right font-mono font-semibold ${r.delta==null?'text-gray-300':r.delta>0?'text-red-500':'text-emerald-600'}`}>
                        {r.delta==null ? '—' : `${r.delta>0?'+':''}${r.delta.toFixed(2)}`}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default Reports